import { AuthState } from '../features/auth/authSlice';

const LOCAL_KEY = 'state';
/**
 * The in-progress booking (address, vehicle, services, add-ons, schedule
 * choices) lives in sessionStorage: it survives a refresh or an accidental
 * back-swipe, but not closing the tab, so nobody picks up a week-old cart.
 */
const SESSION_KEY = 'bookingState';
/**
 * `postalCode` is the booking's too. It used to live in localStorage, shared
 * by every tab, so a second tab's address made the first tab ask for times
 * with the other tab's postal code (QA 2026-09-25 #28).
 */
export const PERSISTED_BOOKING_SLICES = [
  'postalCode',
  'serviceLocation',
  'car',
  'serviceTypes',
  'addOns',
  'booking',
  'customer',
  'consumer',
] as const;

const read = (storage: Storage, key: string) => {
  try {
    const raw = storage.getItem(key);
    return raw === null ? undefined : JSON.parse(raw);
  } catch (error) {
    return undefined;
  }
};

export const loadState = () => {
  const stored = read(localStorage, LOCAL_KEY);
  // Only the login is shared between tabs; a postal code an older build
  // left here belongs to whichever tab wrote it last, so it is ignored.
  let local: Record<string, unknown> | undefined;
  if (stored && typeof stored === 'object') {
    const copy: Record<string, unknown> = { ...stored };
    delete copy.postalCode;
    local = copy;
  }
  const session = read(sessionStorage, SESSION_KEY);
  if (!local && !session) return undefined;
  return { ...(local || {}), ...(session || {}) };
};

export const saveState = (
  state: {
    auth: AuthState
  },
  bookingState?: Record<string, unknown>,
) => {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(state));
  } catch (error) {
    // ignore, maybe log
  }
  if (bookingState) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(bookingState));
    } catch (error) {
      // ignore: private mode or quota; the booking still works in memory
    }
  }
};
